import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { TokeniserIFrameMessage } from '@shared/models/tokeniser-iframe-message';
import { SafeUrlPipe } from '@shared/pipes/safe-url.pipe';
import { ThemeService } from '@shared/services/theme.service';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import { TokenizerErrorListenerDirective } from '@shared/shared/directives/tokenizer-error-listener.directive';
import { TokenizerMessageListenerDirective } from '@shared/shared/directives/tokenizer-message-listener.directive';
import { TokeniserSite } from '@shared/shared/enums/tokeniser-site.enum';
import { TokeniserService, TokeniserURL } from '@shared/shared/services/tokeniser.service';
import { UpdateMemberProfile, getMemberProfileFeatureState } from '@shared/store/member-profile';
import { MemberProfileState } from '@shared/store/member-profile/reducers/member-profile.reducers';
import { getBalance } from '@shared/store/pay-with-points/pay-with-points.actions';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { PayWithPointsBalanceComponent } from '../pay-with-points-balance/pay-with-points-balance.component';

@Component({
  selector: 'ten-pay-with-points',
  templateUrl: './pay-with-points.component.html',
  styleUrls: ['./pay-with-points.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    PayWithPointsBalanceComponent,
    CommonModule,
    MatButtonModule,
    TokenizerMessageListenerDirective,
    TokenizerErrorListenerDirective,
    AccessibleClickDirective,
    SafeUrlPipe,
  ],
})
export class PayWithPointsComponent implements OnInit, OnDestroy {
  @Input() public showDescription = false;
  @Input() public showCurrencyEquivalent = false;
  @Input() public points: number;
  @Input() public fiatAmount: number;
  @Input() public fiatCurrency: string;
  @Input() public profile = false;
  public url: TokeniserURL;
  public token: string;
  public memberProfile$: Observable<MemberProfileState>;

  private unsubscribe$ = new Subject<void>();

  @ViewChild('iframe') public iframe: ElementRef<HTMLIFrameElement>;

  constructor(
    private tokeniserService: TokeniserService,
    private store: Store<MemberProfileState>,
    private themeService: ThemeService
  ) {}

  public ngOnInit() {
    this.url = this.tokeniserService.getTokeniserURL(TokeniserSite.PlatformPayWithPoints);
    this.memberProfile$ = this.store.select(getMemberProfileFeatureState);
    this.memberProfile$
      .pipe(
        map((memberProfile) => memberProfile.pay_with_points_card),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((cardToken) => {
        if (cardToken) {
          this.store.dispatch(getBalance());
        }
      });
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
  }

  public setCustomVariables() {
    if (this.iframe) {
      const secondaryColorName = '--secondary-color';
      const secondaryColorValue = this.themeService.getCurrentCssVariable(secondaryColorName);

      this.iframe.nativeElement.contentWindow.postMessage(
        { type: 'ten-custom-variables', value: { [secondaryColorName]: secondaryColorValue } },
        '*'
      );
    }
  }

  public onMessage(message: TokeniserIFrameMessage) {
    const error = message.tokeniserError;
    const token = message.allFieldValue?.cardNumberToken;
    const shouldReset = message.anyloading || !message.allFieldsValid;

    if (error) {
      this.url.handleFetchError(error, { cardNumberToken: this.token });
    } else if (shouldReset) {
      this.token = undefined;
    } else if (token) {
      this.token = token;
    }
  }

  public addNewCard() {
    this.store.dispatch(new UpdateMemberProfile({ pay_with_points_card: this.token }));
  }
}
